import React, { useState } from 'react'
import type { PresentationOptionV2, PresentationSectionV2 } from 'paintscout'

import { MenuItem, PopupMenu, makeStyles, BlocksIcon, NoFlagIcon, PasteIcon } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { ConfirmationDialog, PresEditorTooltip } from '../components'
import { AddButton } from '../components/ButtonPopupMenu'
import {
  addPresentationNextSection,
  copySectionWithNewKeys,
  deletePresentationNextSection,
  findPage,
  updatePresentationNextSection
} from '@paintscout/util/builder'

import EditOutlineIcon from '@material-ui/icons/EditOutlined'
import AddIcon from '@material-ui/icons/Add'
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import { useFormikContext } from 'formik'
import { usePresentation } from '../../context'
import { EditPageTemplateDialog, PresentationSectionSelectDialog } from '../dialogs'
import { useSnackbar } from 'notistack'
import classnames from 'classnames'
import { PRESENTATION_LABEL } from '@paintscout/util/constants'
import { useDialogs } from '@paintscout/ui-business'

const useStyles = makeStyles<Theme, SectionDividerMenuProps & { sidebarWidth: number }>(
  (theme) => ({
    root: {},
    menuItemDivider: {
      padding: 0,
      height: 0,
      minHeight: 0,
      paddingBottom: theme.spacing(),
      marginBottom: theme.spacing(),
      borderBottom: `1px solid ${theme.palette.grey[300]}`
    },
    menuToggleWrapper: () => ({
      position: 'relative',
      height: theme.spacing(8),
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      textAlign: 'center',
      color: theme.palette.grey[700],
      '&:before, &:after': {
        content: '""',
        borderBottom: `1px dashed ${theme.palette.grey[700]}`
      },
      '&:before': {
        flexBasis: '20px',
        margin: '0 10px 0 -32px'
      },
      '&:after': {
        flex: 1,
        margin: '0 -32px 0 20px'
      },
      '&:hover, &$menuToggleWrapperMenuOpen': {
        '&:before, &:after': {
          borderBottom: `2px solid ${theme.palette.presentation.pageBorder.hover}`
        },
        '& $pageInfo': {
          color: theme.palette.presentation.pageBorder.hover,
          fontWeight: 500
        },
        '& $menuToggle': {
          color: theme.palette.presentation.pageBorder.hover,
          borderColor: theme.palette.presentation.pageBorder.hover,
          borderWidth: '2px',
          borderStyle: 'solid',
          backgroundColor: theme.palette.grey['100']
        }
      }
    }),
    menuToggleWrapperMenuOpen: {
      // needed for above
    },
    pageInfo: {
      display: 'flex',
      alignItems: 'center',
      textAlign: 'center',
      '& svg': {
        fontSize: '1rem'
      }
    },
    menuToggle: {
      position: 'absolute',
      left: 0,
      right: 0,
      margin: '0 auto',
      width: 'fit-content',
      maxWidth: 34,
      borderWidth: 1,
      backgroundColor: theme.palette.common.white,
      color: theme.palette.grey[700],
      borderColor: theme.palette.grey[700],
      borderStyle: 'dashed'
    },
    pageTitle: {
      position: 'relative',
      top: -1
    },
    navIcon: {
      marginLeft: theme.spacing(1)
    },
    editIcon: {
      marginRight: theme.spacing(1)
    },
    deleteIcon: {
      '& svg': {
        color: 'red'
      },
      '&:hover': {
        '& svg': {
          fill: 'red'
        }
      }
    },
    tooltipPlacement: {
      marginBottom: 0,
      marginTop: 0
    },
    tooltipBadgeRoot: {
      display: 'block'
    },
    popupMenuRoot: {
      width: '100%'
    }
  }),
  { name: 'SectionDividerMenu' }
)

/**
 * The dashed divider menu between sections on the canvas (add / rename / copy / paste / delete a
 * **section**). Named SectionDividerMenu because this level was called a "page" in v2 before #21990 — the file
 * rename rides along with the sections/ → blocks/ pass.
 *
 * `pageKey` is the page the sections belong to; `index` is the position within it, or -1 for the
 * "end of presentation" affordance that appends.
 */
export interface SectionDividerMenuProps {
  classes?: StyleClasses<typeof useStyles>
  component?: React.ReactElement
  index: number
  pageKey: string
  tooltipTitle?: string
}

function SectionDividerMenu(props: SectionDividerMenuProps) {
  const { component, index, pageKey, tooltipTitle } = props
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { setValues, submitForm, values } = useFormikContext<PresentationOptionV2>()
  const {
    onPresentationPageChange,
    onSidebarPageChange,
    setIsSaving,
    sidebarPage,
    sidebarWidth = 0
  } = usePresentation()
  const classes = useStyles({ ...props, sidebarWidth })
  const sections = findPage(values, pageKey)?.page?.sections ?? []
  const endOfPresentation = index === -1
  const section = !endOfPresentation ? sections[index] : null
  const { openDialog, dismissDialog } = useDialogs()
  const { enqueueSnackbar } = useSnackbar()

  const copiedPage = window.sessionStorage.getItem('copiedSection')

  const insertIndex = endOfPresentation ? sections.length : index

  const handleEditPage = async (newSection: PresentationSectionV2) => {
    setValues(updatePresentationNextSection({ presentation: values, section: newSection }))
    submitForm()
  }

  const handleAddPage = (newSection: PresentationSectionV2) => {
    setValues(addPresentationNextSection({ presentation: values, pageKey, section: newSection, index: insertIndex }))
    submitForm()
  }

  const handleAddNewPage = () => {
    openDialog(PresentationSectionSelectDialog, {
      onConfirm: (newSection: PresentationSectionV2) => {
        handleAddPage(newSection)
        onPresentationPageChange(newSection.key)
        dismissDialog()
      },
      presentation: values,
      onCancel: () => {
        dismissDialog()
      }
    } as any)
  }

  const handleRenamePage = async (_ev: React.MouseEvent<HTMLElement, MouseEvent>) => {
    openDialog(EditPageTemplateDialog, {
      page: section,
      isNew: false,
      onConfirm: async (newSection: PresentationSectionV2) => {
        setIsSaving(true)
        dismissDialog()
        await handleEditPage(newSection)
        setIsSaving(false)
      },
      onCancel: dismissDialog
    })
  }

  const handleRemovePage = async () => {
    openDialog(ConfirmationDialog, {
      title: 'Confirm Remove Section',
      message: `This section will be removed from the ${PRESENTATION_LABEL}.`,
      noLabel: 'Cancel',
      onConfirm: () => {
        setValues(deletePresentationNextSection({ presentation: values, sectionKey: section.key }))
        submitForm()
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  const handleCopyPage = () => {
    enqueueSnackbar('Section Copied', { variant: 'success' })
    window.sessionStorage.setItem('copiedSection', JSON.stringify(section))
  }

  const handlePastePage = () => {
    // re-key on paste so pasting the same copied section twice doesn't collide
    const pasted = copySectionWithNewKeys(JSON.parse(copiedPage))

    setValues(addPresentationNextSection({ presentation: values, pageKey, section: pasted, index: insertIndex }))
    submitForm()
  }

  const handleOpen = () => {
    if (sidebarPage !== 'pages') onSidebarPageChange('pages')
    setIsMenuOpen(true)
  }

  return (
    <PresEditorTooltip
      title={tooltipTitle}
      disableHoverListener={isMenuOpen}
      disableFocusListener={isMenuOpen}
      disableTouchListener={isMenuOpen}
      classes={{
        tooltipPlacementTop: classes.tooltipPlacement,
        tooltipPlacementBottom: classes.tooltipPlacement,
        badgeRoot: classes.tooltipBadgeRoot
      }}
      {...(!tooltipTitle && {
        open: false
      })}
    >
      <PopupMenu
        TransitionProps={{ onEnter: () => handleOpen(), onExit: () => setIsMenuOpen(false) }}
        component={
          component ?? (
            <div
              className={classnames(classes.menuToggleWrapper, {
                [classes.menuToggleWrapperMenuOpen]: isMenuOpen
              })}
            >
              <div className={classes.pageInfo}>
                <BlocksIcon className={classes.editIcon} />
                <div className={classes.pageTitle}>
                  {endOfPresentation ? `End of ${PRESENTATION_LABEL}` : section.title}
                </div>
                {!section?.advanced?.useForNav && !endOfPresentation && <NoFlagIcon className={classes.navIcon} />}
              </div>
              <AddButton
                buttonSize={'small'}
                id={`add-page-${index}`}
                className={classes.menuToggle}
                buttonVariant={'outlined'}
              />
            </div>
          )
        }
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        classes={{ root: classes.popupMenuRoot }}
      >
        {[
          <MenuItem key={'add'} icon={AddIcon} onClick={handleAddNewPage}>
            Add Section Above
          </MenuItem>,
          ...(copiedPage && endOfPresentation
            ? [
                <MenuItem key={'divider'} disabled classes={{ root: classes.menuItemDivider }} />,
                <MenuItem key={'paste'} icon={PasteIcon} onClick={handlePastePage}>
                  Paste Section
                </MenuItem>
              ]
            : []),
          ...(!endOfPresentation
            ? [
                <MenuItem key={'divider'} disabled classes={{ root: classes.menuItemDivider }} />,
                <MenuItem key={'rename'} icon={EditOutlineIcon} onClick={handleRenamePage}>
                  Rename
                </MenuItem>,
                <MenuItem key={'copy'} icon={FileCopyOutlinedIcon} onClick={handleCopyPage}>
                  Copy Section
                </MenuItem>,
                ...(copiedPage
                  ? [
                      <MenuItem key={'paste'} icon={PasteIcon} onClick={handlePastePage}>
                        Paste Section
                      </MenuItem>
                    ]
                  : []),
                <MenuItem
                  key={'delete'}
                  icon={DeleteOutlineOutlinedIcon}
                  classes={{ root: classes.deleteIcon }}
                  onClick={handleRemovePage}
                  disabled={sections.length === 1}
                >
                  Delete Section
                </MenuItem>
              ]
            : [])
        ]}
      </PopupMenu>
    </PresEditorTooltip>
  )
}

export default SectionDividerMenu
